// Inloggningen mot Cognitos användarpool från kontot och appen (ADR 0083).
// Anropen går direkt till Cognitos API med fetch, så att varken webbläsaren
// eller appen behöver AWS SDK. Lösenordet skickas bara till Cognito, med TLS.
//
// Flödet: e-post och lösenord ger antingen token eller ett steg till. En
// inbjuden användare väljer först ett nytt lösenord. Alla har MFA med en app
// för engångskoder (TOTP): första gången registreras appen med en QR-kod,
// sedan anges en kod vid inloggningen. Efter koden kommer Cognito ihåg
// webbläsaren som en enhet i 30 dagar, och då behövs ingen kod (ADR 0108).

/** Hur länge en webbläsare slipper koden efter en inloggning med kod. */
export const KOM_IHAG_DAGAR = 30;
const KOM_IHAG_MS = KOM_IHAG_DAGAR * 24 * 60 * 60 * 1000;

/** En ihågkommen enhet: Cognitos nycklar och enhetens hemlighet. */
export interface Enhet {
  nyckel: string;
  grupp: string;
  losenord: string;
  /** När koden angavs, i millisekunder sedan 1970. */
  sparad: number;
}

/** Där webbläsaren sparar sina enheter, en per e-postadress. */
export interface Enheter {
  las(epost: string): Enhet | null;
  spara(epost: string, enhet: Enhet | null): void;
}

export interface CognitoKonfig {
  region: string;
  /** Klienten i användarpoolen: en för kontot och en för appen. */
  clientId: string;
  fetch?: typeof fetch;
  /** För testerna. */
  nu?: () => number;
  /** Enheterna, när webbläsaren ska slippa koden i 30 dagar. Saknas i appen. */
  enheter?: Enheter;
  /** Namnet på enheten i Cognito, till exempel webbläsaren. */
  enhetNamn?: string;
}

export interface Tokens {
  accessToken: string;
  /** Saknas när en token förnyas, eftersom samma refresh-token gäller. */
  refreshToken?: string;
  /** När access-token går ut, i millisekunder sedan 1970. */
  expiresAt: number;
  /** Enheten som token hör till. Skickas med när token förnyas. */
  deviceKey?: string;
}

export type Steg =
  | { typ: 'inloggad'; tokens: Tokens }
  /** Inbjuden användare med tillfälligt lösenord. */
  | { typ: 'nytt-losenord'; session: string }
  | { typ: 'mfa-kod'; session: string }
  /** Första inloggningen: appen för engångskoder registreras. */
  | { typ: 'mfa-registrera'; session: string; hemlighet: string; otpauth: string };

export class CognitoFel extends Error {
  constructor(
    /** Cognitos feltyp, till exempel NotAuthorizedException, eller natverk. */
    readonly kod: string,
    message: string,
  ) {
    super(message);
    this.name = 'CognitoFel';
  }
}

const UTFARDARE = 'LeadLedger';

/** Texten till användaren för ett fel från Cognito. */
export function felText(kod: string, meddelande = ''): string {
  switch (kod) {
    case 'NotAuthorizedException':
      if (/session/i.test(meddelande)) return 'Det tog för lång tid. Börja om med att logga in.';
      if (/refresh token/i.test(meddelande)) return 'Inloggningen har gått ut. Logga in igen.';
      return 'Fel e-postadress eller lösenord.';
    case 'UserNotFoundException':
      return 'Fel e-postadress eller lösenord.';
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return 'Koden stämmer inte. Kontrollera appen och försök igen.';
    case 'ExpiredCodeException':
      return 'Koden har gått ut. Begär en ny.';
    case 'InvalidPasswordException':
      return 'Lösenordet behöver vara minst 12 tecken.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'För många försök. Vänta en stund och försök igen.';
    case 'PasswordResetRequiredException':
      return 'Du behöver välja ett nytt lösenord. Välj Glömt lösenordet.';
    case 'natverk':
      return 'Inloggningen svarar inte. Kontrollera anslutningen och försök igen.';
    default:
      return 'Inloggningen misslyckades. Försök igen.';
  }
}

interface AuthSvar {
  AuthenticationResult?: {
    AccessToken: string;
    RefreshToken?: string;
    ExpiresIn: number;
    NewDeviceMetadata?: { DeviceKey: string; DeviceGroupKey: string };
  };
  ChallengeName?: string;
  ChallengeParameters?: Record<string, string>;
  Session?: string;
}

/** Fel som gäller användaren och inte enheten. Då försöker inloggningen inte igen utan enheten. */
const ANVANDARFEL = new Set([
  'NotAuthorizedException',
  'UserNotFoundException',
  'PasswordResetRequiredException',
  'LimitExceededException',
  'TooManyRequestsException',
  'TooManyFailedAttemptsException',
  'natverk',
]);

export function createCognito({
  region,
  clientId,
  fetch: f = fetch,
  nu = Date.now,
  enheter,
  enhetNamn = 'Webbläsare',
}: CognitoKonfig) {
  const adress = `https://cognito-idp.${region}.amazonaws.com/`;

  async function anropa<T>(atgard: string, kropp: Record<string, unknown>): Promise<T> {
    let svar: Response;
    try {
      svar = await f(adress, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': `AWSCognitoIdentityProviderService.${atgard}`,
        },
        body: JSON.stringify(kropp),
      });
    } catch {
      throw new CognitoFel('natverk', felText('natverk'));
    }
    const data = (await svar.json().catch(() => ({}))) as Record<string, unknown>;
    if (!svar.ok) {
      const typ =
        String(data.__type ?? data.code ?? 'okant')
          .split('#')
          .pop() ?? 'okant';
      const meddelande = String(data.message ?? data.Message ?? '');
      throw new CognitoFel(typ, felText(typ, meddelande));
    }
    return data as T;
  }

  const tokens = (
    r: NonNullable<AuthSvar['AuthenticationResult']>,
    deviceKey?: string,
  ): Tokens => ({
    accessToken: r.AccessToken,
    ...(r.RefreshToken ? { refreshToken: r.RefreshToken } : {}),
    expiresAt: nu() + r.ExpiresIn * 1000,
    ...(deviceKey ? { deviceKey } : {}),
  });

  /** Enheten för adressen, om koden angavs för mindre än 30 dagar sedan. */
  function giltigEnhet(epost: string): Enhet | null {
    const enhet = enheter?.las(epost) ?? null;
    return enhet && nu() - enhet.sparad < KOM_IHAG_MS ? enhet : null;
  }

  /**
   * Cognito kommer ihåg webbläsaren efter en inloggning med kod. Den gamla
   * enheten glöms, så att bara en per webbläsare finns kvar. Ger enhetens
   * nyckel, eller undefined om det inte gick.
   */
  async function kommIhag(
    accessToken: string,
    { DeviceKey, DeviceGroupKey }: { DeviceKey: string; DeviceGroupKey: string },
    epost: string,
  ): Promise<string | undefined> {
    if (!enheter) return undefined;
    try {
      const { losenord, verifier } = await nyEnhet(DeviceGroupKey, DeviceKey);
      const svar = await anropa<{ UserConfirmationNecessary?: boolean }>('ConfirmDevice', {
        AccessToken: accessToken,
        DeviceKey,
        DeviceSecretVerifierConfig: verifier,
        DeviceName: enhetNamn,
      });
      if (svar.UserConfirmationNecessary) {
        await anropa('UpdateDeviceStatus', {
          AccessToken: accessToken,
          DeviceKey,
          DeviceRememberedStatus: 'remembered',
        });
      }
      const gammal = enheter.las(epost);
      enheter.spara(epost, { nyckel: DeviceKey, grupp: DeviceGroupKey, losenord, sparad: nu() });
      if (gammal && gammal.nyckel !== DeviceKey) {
        await anropa('ForgetDevice', { AccessToken: accessToken, DeviceKey: gammal.nyckel }).catch(
          () => undefined,
        );
      }
      return DeviceKey;
    } catch {
      // Utan enheten fungerar inloggningen som förut, med kod varje gång.
      return undefined;
    }
  }

  /** Inloggningen med en ihågkommen enhet, i stället för koden (DEVICE_SRP_AUTH). */
  async function medEnhet(svar: AuthSvar, epost: string, enhet: Enhet): Promise<Steg> {
    const { a, A } = nyttA();
    const anvandare = svar.ChallengeParameters?.USERNAME ?? epost;
    const fraga = await anropa<AuthSvar>('RespondToAuthChallenge', {
      ClientId: clientId,
      ChallengeName: 'DEVICE_SRP_AUTH',
      Session: svar.Session,
      ChallengeResponses: { USERNAME: anvandare, DEVICE_KEY: enhet.nyckel, SRP_A: A.toString(16) },
    });
    const p = fraga.ChallengeParameters ?? {};
    if (
      fraga.ChallengeName !== 'DEVICE_PASSWORD_VERIFIER' ||
      !p.SRP_B ||
      !p.SALT ||
      !p.SECRET_BLOCK
    ) {
      throw new CognitoFel('enhet', 'Enheten kunde inte användas.');
    }
    const tid = tidsstampel(new Date(nu()));
    const signatur = await enhetensSignatur({
      grupp: enhet.grupp,
      nyckel: enhet.nyckel,
      losenord: enhet.losenord,
      a,
      A,
      srpB: p.SRP_B,
      saltHex: p.SALT,
      hemligtBlock: p.SECRET_BLOCK,
      tid,
    });
    const klart = await anropa<AuthSvar>('RespondToAuthChallenge', {
      ClientId: clientId,
      ChallengeName: 'DEVICE_PASSWORD_VERIFIER',
      Session: fraga.Session,
      ChallengeResponses: {
        USERNAME: p.USERNAME ?? anvandare,
        PASSWORD_CLAIM_SECRET_BLOCK: p.SECRET_BLOCK,
        TIMESTAMP: tid,
        PASSWORD_CLAIM_SIGNATURE: signatur,
        DEVICE_KEY: enhet.nyckel,
      },
    });
    if (!klart.AuthenticationResult) throw new CognitoFel('enhet', 'Enheten kunde inte användas.');
    return { typ: 'inloggad', tokens: tokens(klart.AuthenticationResult, enhet.nyckel) };
  }

  async function nastaSteg(svar: AuthSvar, epost: string, ihag = false): Promise<Steg> {
    const r = svar.AuthenticationResult;
    if (r) {
      const deviceKey =
        ihag && r.NewDeviceMetadata
          ? await kommIhag(r.AccessToken, r.NewDeviceMetadata, epost)
          : undefined;
      return { typ: 'inloggad', tokens: tokens(r, deviceKey) };
    }
    const session = svar.Session ?? '';
    switch (svar.ChallengeName) {
      case 'NEW_PASSWORD_REQUIRED':
        return { typ: 'nytt-losenord', session };
      case 'SOFTWARE_TOKEN_MFA':
        return { typ: 'mfa-kod', session };
      case 'MFA_SETUP': {
        const { SecretCode, Session } = await anropa<{ SecretCode: string; Session: string }>(
          'AssociateSoftwareToken',
          { Session: session },
        );
        const etikett = encodeURIComponent(`${UTFARDARE}:${epost}`);
        return {
          typ: 'mfa-registrera',
          session: Session,
          hemlighet: SecretCode,
          otpauth: `otpauth://totp/${etikett}?secret=${SecretCode}&issuer=${UTFARDARE}`,
        };
      }
      default:
        throw new CognitoFel(
          svar.ChallengeName ?? 'okant',
          'Inloggningen kräver ett steg som LeadLedger inte stöder. Kontakta oss.',
        );
    }
  }

  const svaraPa = async (
    ChallengeName: string,
    Session: string,
    ChallengeResponses: Record<string, string>,
    epost: string,
    ihag = false,
  ) =>
    nastaSteg(
      await anropa<AuthSvar>('RespondToAuthChallenge', {
        ClientId: clientId,
        ChallengeName,
        Session,
        ChallengeResponses,
      }),
      epost,
      ihag,
    );

  const initiera = (epost: string, losenord: string, enhet: Enhet | null) =>
    anropa<AuthSvar>('InitiateAuth', {
      ClientId: clientId,
      AuthFlow: 'USER_PASSWORD_AUTH',
      AuthParameters: {
        USERNAME: epost,
        PASSWORD: losenord,
        ...(enhet ? { DEVICE_KEY: enhet.nyckel } : {}),
      },
    });

  return {
    async loggaIn(epost: string, losenord: string): Promise<Steg> {
      const enhet = giltigEnhet(epost);
      if (enhet) {
        // Går enheten inte att använda glöms den, och inloggningen börjar om med kod.
        let svar: AuthSvar | undefined;
        try {
          svar = await initiera(epost, losenord, enhet);
        } catch (err) {
          if (err instanceof CognitoFel && ANVANDARFEL.has(err.kod)) throw err;
        }
        if (svar?.ChallengeName === 'DEVICE_SRP_AUTH') {
          // Lösenordet stämde. Ett fel här gäller enheten, inte användaren.
          const steg = await medEnhet(svar, epost, enhet).catch(() => undefined);
          if (steg) return steg;
        } else if (svar) {
          return nastaSteg(svar, epost);
        }
        enheter?.spara(epost, null);
      }
      return nastaSteg(await initiera(epost, losenord, null), epost);
    },

    nyttLosenord(session: string, epost: string, losenord: string): Promise<Steg> {
      return svaraPa(
        'NEW_PASSWORD_REQUIRED',
        session,
        { USERNAME: epost, NEW_PASSWORD: losenord },
        epost,
      );
    },

    /** Koden från appen. Med kommIhag slipper webbläsaren koden i 30 dagar. */
    mfaKod(session: string, epost: string, kod: string, kommIhag = false): Promise<Steg> {
      return svaraPa(
        'SOFTWARE_TOKEN_MFA',
        session,
        { USERNAME: epost, SOFTWARE_TOKEN_MFA_CODE: kod.replace(/\s/g, '') },
        epost,
        kommIhag,
      );
    },

    async registreraMfa(
      session: string,
      epost: string,
      kod: string,
      kommIhag = false,
    ): Promise<Steg> {
      const { Status, Session } = await anropa<{ Status: string; Session: string }>(
        'VerifySoftwareToken',
        { Session: session, UserCode: kod.replace(/\s/g, ''), FriendlyDeviceName: UTFARDARE },
      );
      if (Status !== 'SUCCESS') {
        throw new CognitoFel('CodeMismatchException', felText('CodeMismatchException'));
      }
      return svaraPa('MFA_SETUP', Session, { USERNAME: epost }, epost, kommIhag);
    },

    /**
     * Ny access-token med refresh-token. Kastar om inloggningen har gått ut.
     * En token från en ihågkommen enhet förnyas med enhetens nyckel.
     */
    async fornya(refreshToken: string, deviceKey?: string): Promise<Tokens> {
      const svar = await anropa<AuthSvar>('InitiateAuth', {
        ClientId: clientId,
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
          ...(deviceKey ? { DEVICE_KEY: deviceKey } : {}),
        },
      });
      if (!svar.AuthenticationResult) {
        throw new CognitoFel(
          'NotAuthorizedException',
          felText('NotAuthorizedException', 'refresh token'),
        );
      }
      return tokens(svar.AuthenticationResult, deviceKey);
    },

    /** Glömmer webbläsaren för adressen, så att nästa inloggning kräver kod. */
    glomEnhet(epost: string): void {
      enheter?.spara(epost, null);
    },

    /** Skickar en kod till e-postadressen. Säger inte om adressen finns. */
    async glomtLosenord(epost: string): Promise<void> {
      await anropa('ForgotPassword', { ClientId: clientId, Username: epost });
    },

    async valjLosenordMedKod(epost: string, kod: string, losenord: string): Promise<void> {
      await anropa('ConfirmForgotPassword', {
        ClientId: clientId,
        Username: epost,
        ConfirmationCode: kod.replace(/\s/g, ''),
        Password: losenord,
      });
    },

    /** Gör refresh-token ogiltig, så att inloggningen inte kan förnyas. */
    async loggaUt(refreshToken: string): Promise<void> {
      await anropa('RevokeToken', { ClientId: clientId, Token: refreshToken });
    },
  };
}

export type Cognito = ReturnType<typeof createCognito>;

/** Inloggningens inställningar från API:t (GET /v1/public/auth-config). */
export type AuthConfig =
  | { provider: 'local' }
  | {
      provider: 'cognito';
      region: string;
      userPoolId: string;
      webClientId: string;
      appClientId: string;
    };

// SRP för de ihågkomna enheterna (ADR 0108). En enhet har en egen hemlighet
// som aldrig lämnar webbläsaren: Cognito får bara en verifierare, och vid
// inloggningen bevisar webbläsaren att den känner hemligheten utan att skicka
// den. Samma beräkning som i AWS egna bibliotek, med BigInt och Web Crypto, så
// att inget extra paket behövs. Allt ligger i den här filen, eftersom appens
// Metro läser paketet från källkoden.

/** Gruppen på 3072 bitar från RFC 5054, som Cognito använder. */
const N_HEX =
  'FFFFFFFFFFFFFFFFC90FDAA22168C234C4C6628B80DC1CD129024E088A67CC74020BBEA63B139B22514A08798E3404DD' +
  'EF9519B3CD3A431B302B0A6DF25F14374FE1356D6D51C245E485B576625E7EC6F44C42E9A637ED6B0BFF5CB6F406B7ED' +
  'EE386BFB5A899FA5AE9F24117C4B1FE649286651ECE45B3DC2007CB8A163BF0598DA48361C55D39A69163FA8FD24CF5F' +
  '83655D23DCA3AD961C62F356208552BB9ED529077096966D670C354E4ABC9804F1746C08CA18217C32905E462E36CE3B' +
  'E39E772C180E86039B2783A2EC07A28FB5C55DF06F4C52C9DE2BCBF6955817183995497CEA956AE515D2261898FA0510' +
  '15728E5A8AAAC42DAD33170D04507A33A85521ABDF1CBA64ECFB850458DBEF0A8AEA71575D060C7DB3970F85A6E1E4C7' +
  'ABF5AE8CDB0933D71E8C94E04A25619DCEE3D2261AD2EE6BF12FFA06D98A0864D87602733EC86A64521F2B18177B200C' +
  'BBE117577A615D6C770988C0BAD946E208E24FA074E5AB3143DB5BFCE0FD108E4B82D120A93AD2CAFFFFFFFFFFFFFFFF';

export const N = BigInt(`0x${N_HEX}`);
export const g = 2n;

const subtle = () => {
  const s = globalThis.crypto?.subtle;
  if (!s) throw new Error('Web Crypto saknas');
  return s;
};

/** a^b mod m. */
export function modPow(bas: bigint, exponent: bigint, m: bigint): bigint {
  let resultat = 1n;
  let b = ((bas % m) + m) % m;
  let e = exponent;
  while (e > 0n) {
    if (e & 1n) resultat = (resultat * b) % m;
    b = (b * b) % m;
    e >>= 1n;
  }
  return resultat;
}

/**
 * Talet som hex med jämnt antal tecken, och en inledande nolla när den
 * första biten är satt, så att talet inte läses som negativt. Som Javas BigInteger.
 */
export function padHex(tal: bigint): string {
  let hex = tal.toString(16);
  if (hex.length % 2 === 1) hex = `0${hex}`;
  else if ('89abcdef'.includes(hex[0] ?? '')) hex = `00${hex}`;
  return hex;
}

export function hexTillBytes(hex: string): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return bytes;
}

const bytesTillHex = (bytes: Uint8Array) =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

export function base64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function franBase64(text: string): Uint8Array {
  const s = atob(text);
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
}

const utf8 = (text: string) => new TextEncoder().encode(text);

function sammanfoga(...delar: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const ut = new Uint8Array(delar.reduce((n, d) => n + d.length, 0));
  let i = 0;
  for (const d of delar) {
    ut.set(d, i);
    i += d.length;
  }
  return ut;
}

async function sha256(bytes: Uint8Array): Promise<string> {
  const kopia = sammanfoga(bytes);
  return bytesTillHex(new Uint8Array(await subtle().digest('SHA-256', kopia))).padStart(64, '0');
}

/** SHA-256 av en text, som hex. */
export const hash = (text: string) => sha256(utf8(text));
/** SHA-256 av bytes som är skrivna som hex, som hex. */
export const hexHash = (hex: string) => sha256(hexTillBytes(hex));

async function hmac(nyckel: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const k = await subtle().importKey(
    'raw',
    sammanfoga(nyckel),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return new Uint8Array(await subtle().sign('HMAC', k, sammanfoga(data)));
}

export function slumpBytes(antal: number): Uint8Array<ArrayBuffer> {
  return globalThis.crypto.getRandomValues(new Uint8Array(antal));
}

let kCache: Promise<bigint> | undefined;
/** k = H(N | g). */
export const k = () =>
  (kCache ??= hexHash(`${padHex(N)}${padHex(g)}`).then((h) => BigInt(`0x${h}`)));

/** Hemligheten och verifieraren för en ny enhet, till ConfirmDevice. */
export async function nyEnhet(grupp: string, nyckel: string) {
  const losenord = base64(slumpBytes(40));
  const salt = padHex(BigInt(`0x${bytesTillHex(slumpBytes(16))}`));
  const x = BigInt(`0x${await hexHash(salt + (await hash(`${grupp}${nyckel}:${losenord}`)))}`);
  const verifierare = modPow(g, x, N);
  return {
    losenord,
    verifier: {
      Salt: base64(hexTillBytes(salt)),
      PasswordVerifier: base64(hexTillBytes(padHex(verifierare))),
    },
  };
}

/** Ett hemligt a och A = g^a mod N till inloggningen. */
export function nyttA(): { a: bigint; A: bigint } {
  for (;;) {
    const a = BigInt(`0x${bytesTillHex(slumpBytes(128))}`) % N;
    const A = modPow(g, a, N);
    if (A % N !== 0n) return { a, A };
  }
}

/** Tidsstämpeln i Cognitos format, till exempel "Tue Oct 6 09:05:01 UTC 2026", utan nolla före dagen. */
export function tidsstampel(nu: Date): string {
  const dagar = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const manader = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const tva = (n: number) => String(n).padStart(2, '0');
  return `${dagar[nu.getUTCDay()]} ${manader[nu.getUTCMonth()]} ${nu.getUTCDate()} ${tva(
    nu.getUTCHours(),
  )}:${tva(nu.getUTCMinutes())}:${tva(nu.getUTCSeconds())} UTC ${nu.getUTCFullYear()}`;
}

/**
 * Svaret på DEVICE_PASSWORD_VERIFIER: signaturen som visar att webbläsaren
 * känner enhetens hemlighet.
 */
export async function enhetensSignatur({
  grupp,
  nyckel,
  losenord,
  a,
  A,
  srpB,
  saltHex,
  hemligtBlock,
  tid,
}: {
  grupp: string;
  nyckel: string;
  losenord: string;
  a: bigint;
  A: bigint;
  srpB: string;
  saltHex: string;
  hemligtBlock: string;
  tid: string;
}): Promise<string> {
  const B = BigInt(`0x${srpB}`);
  if (B % N === 0n) throw new Error('Ogiltigt B från Cognito');
  const u = BigInt(`0x${await hexHash(padHex(A) + padHex(B))}`);
  if (u === 0n) throw new Error('Ogiltigt u');
  const salt = padHex(BigInt(`0x${saltHex}`));
  const x = BigInt(`0x${await hexHash(salt + (await hash(`${grupp}${nyckel}:${losenord}`)))}`);
  const kv = await k();
  const S = modPow(B - kv * modPow(g, x, N), a + u * x, N);
  // HKDF med u som salt och S som nyckelmaterial, 16 bytes.
  const prk = await hmac(hexTillBytes(padHex(u)), hexTillBytes(padHex(S)));
  const nyckelBytes = (
    await hmac(prk, sammanfoga(utf8('Caldera Derived Key'), Uint8Array.of(1)))
  ).slice(0, 16);
  const meddelande = sammanfoga(utf8(grupp), utf8(nyckel), franBase64(hemligtBlock), utf8(tid));
  return base64(await hmac(nyckelBytes, meddelande));
}
